import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Search, FileText, Calendar, BookOpen
} from 'lucide-react';

export default function GeneralLedgerPage() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [, setAccountsLoading] = useState(false);
  const [, setPagination] = useState({ page: 1, limit: 50 });

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccountId) {
      fetchLedger();
    }
  }, [selectedAccountId, startDate, endDate]);

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const res = await api.get('/accounts', { params: { limit: 200 } });
      setAccounts(res.data.data?.accounts || res.data.data || []);
    } catch (error) {
      handleApiError(error);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchLedger = async () => {
    if (!selectedAccountId) return;
    setLoading(true);
    try {
      const params = queryParams({
        account_id: selectedAccountId,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      const res = await api.get('/reports/general-ledger', { params });
      setData(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Buku Besar</h1>
          <p className="page-subtitle">Riwayat transaksi per akun (General Ledger)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => toast.success('Export PDF - Coming Soon')} className="btn btn-outline btn-sm">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </button>
          <button onClick={() => toast.success('Export Excel - Coming Soon')} className="btn btn-outline btn-sm">
            <FileText className="h-4 w-4 mr-1.5" /> Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="label">Pilih Akun</label>
              <select
                className="input"
                value={selectedAccountId}
                onChange={(e) => { setSelectedAccountId(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              >
                <option value="">-- Pilih Akun --</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.code || acc.account_code} - {acc.name || acc.account_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Dari Tanggal</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  className="input pl-9"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="label">Sampai Tanggal</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  className="input pl-9"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={fetchLedger} className="btn btn-primary btn-sm" disabled={!selectedAccountId}>
                <Search className="h-4 w-4 mr-1.5" /> Tampilkan
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {!selectedAccountId ? (
        <div className="card">
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Pilih Akun</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Pilih akun untuk melihat buku besar.</p>
          </div>
        </div>
      ) : loading ? (
        <div className="card">
          <div className="card-header">
            <div className="skeleton h-5 w-48" />
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Tanggal</th>
                  <th>Referensi</th>
                  <th>Deskripsi</th>
                  <th className="text-right">Debit</th>
                  <th className="text-right">Kredit</th>
                  <th className="text-right">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(8)].map((_, i) => (
                  <tr key={i}>
                    <td><div className="skeleton h-4 w-24" /></td>
                    <td><div className="skeleton h-4 w-20" /></td>
                    <td><div className="skeleton h-4 w-40" /></td>
                    <td><div className="skeleton h-4 w-20 ml-auto" /></td>
                    <td><div className="skeleton h-4 w-20 ml-auto" /></td>
                    <td><div className="skeleton h-4 w-24 ml-auto" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : !data || !data.transactions || data.transactions.length === 0 ? (
        <div className="card">
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Transaksi</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Tidak ada transaksi untuk akun ini pada periode yang dipilih.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          {/* Account Info */}
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center text-primary-900 font-semibold">
                <BookOpen className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{data.account?.code} - {data.account?.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Saldo Awal: <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(data.opening_balance)}</span>
                  {' | '}Saldo Akhir: <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(data.closing_balance)}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-container rounded-none border-0">
            <table className="table">
              <thead>
                <tr>
                  <th>Tanggal</th>
                  <th>Referensi</th>
                  <th>Deskripsi</th>
                  <th className="text-right">Debit</th>
                  <th className="text-right">Kredit</th>
                  <th className="text-right">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {/* Opening Balance Row */}
                <tr className="bg-gray-50 dark:bg-gray-800/30">
                  <td colSpan="5" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Saldo Awal ({formatDate(startDate)})
                  </td>
                  <td className="text-right">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(data.opening_balance)}</span>
                  </td>
                </tr>
                {data.transactions.map((tx, idx) => (
                  <tr key={tx.journal_id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td>
                      <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(tx.entry_date)}</span>
                    </td>
                    <td>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{tx.doc_number || '-'}</span>
                    </td>
                    <td>
                      <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[200px] block" title={tx.description}>
                        {tx.description || tx.item_description || '-'}
                      </span>
                    </td>
                    <td className="text-right">
                      {tx.debit > 0 ? (
                        <span className="text-sm text-primary-900 dark:text-primary-400">{formatCurrency(tx.debit)}</span>
                      ) : (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      )}
                    </td>
                    <td className="text-right">
                      {tx.credit > 0 ? (
                        <span className="text-sm text-danger-700 dark:text-danger-400">{formatCurrency(tx.credit)}</span>
                      ) : (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      )}
                    </td>
                    <td className="text-right">
                      <span className={`text-sm font-semibold ${tx.balance >= 0 ? 'text-gray-900 dark:text-white' : 'text-danger-600 dark:text-danger-400'}`}>
                        {formatCurrency(tx.balance)}
                      </span>
                    </td>
                  </tr>
                ))}
                {/* Closing Balance Row */}
                <tr className="bg-primary-50 dark:bg-primary-900/20 font-semibold">
                  <td colSpan="5" className="text-sm font-semibold text-gray-900 dark:text-white">
                    Saldo Akhir ({formatDate(endDate)})
                  </td>
                  <td className="text-right">
                    <span className="text-sm font-semibold text-primary-800 dark:text-primary-400">{formatCurrency(data.closing_balance)}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
